// ─────────────────────────────────────────────────────────────────────────────
// polygon.ts — Base polygon with particle animation system
//
// Provides a reusable graph structure (nodes + edges) with autonomous
// particles that hop between nodes, creating momentary edge trails.
//
// Architecture: logic (graph, particles, time evolution) is separated from
// rendering.  Subclasses or composition handle the visual aspect.
//
// Time evolution is async-friendly: call tick(now) each frame to advance
// particle state, then snapshot() to read the current visual frame.
// ─────────────────────────────────────────────────────────────────────────────

// ── Types ────────────────────────────────────────────────────────────────────

export interface PolyNode {
  x: number;
  y: number;
}

export interface PolyEdge {
  a: number;  // node index
  b: number;  // node index
}

// ── Particle state ───────────────────────────────────────────────────────────

export interface Particle {
  /** Node this particle currently occupies (or departed from if jumping). */
  homeNode: number;
  /** 'resting' = sitting on homeNode, 'jumping' = in transit. */
  state: 'resting' | 'jumping';
  /** Target node index (valid when jumping). */
  targetNode: number;
  /** Edge index being traversed (valid when jumping). */
  edgeIdx: number;
  /** Jump start timestamp (ms). */
  jumpStart: number;
  /** Jump duration (ms). */
  jumpDur: number;
  /** true if the particle is bouncing back (target was occupied). */
  bouncing: boolean;
  /** Cooldown until next jump attempt (ms remaining). */
  cooldown: number;
}

// ── Edge trail (visual trace left by a jumping particle) ─────────────────────

export interface EdgeTrail {
  edgeIdx: number;
  /** Direction: +1 = a→b, -1 = b→a. */
  direction: 1 | -1;
  /** 0→1 progress along the edge. */
  head: number;
  /** 0→1 trailing fade position. */
  tail: number;
  /** Opacity multiplier (fades after particle arrives). */
  alpha: number;
  /** Birth time (ms). */
  birth: number;
}

// ── Snapshot — the immutable frame read by renderers ─────────────────────────

export interface PolygonSnapshot {
  nodes: PolyNode[];
  edges: PolyEdge[];
  /** Set of node indices currently occupied by a particle. */
  occupied: Set<number>;
  /** Particles with their interpolated positions. */
  particles: ParticleView[];
  /** Active edge trails (lines being drawn). */
  trails: EdgeTrail[];
}

export interface ParticleView {
  /** Interpolated x position. */
  x: number;
  /** Interpolated y position. */
  y: number;
  /** Index of the node the particle is on or departing from. */
  homeNode: number;
  /** Whether the particle is resting or in-transit. */
  resting: boolean;
}

// ── Configuration ────────────────────────────────────────────────────────────

export interface PolygonConfig {
  /** Number of particles to spawn. */
  particleCount: number;
  /** Base probability of a resting particle attempting a jump (per tick). */
  jumpProbability: number;
  /** Jump duration range [min, max] in ms. */
  jumpDurationRange: [number, number];
  /** Cooldown range [min, max] in ms after a jump completes. */
  cooldownRange: [number, number];
  /** Trail fade time in ms after a jump completes. */
  trailFadeMs: number;
}

export const DEFAULT_POLYGON_CONFIG: PolygonConfig = {
  particleCount: 3,
  jumpProbability: 0.012,
  jumpDurationRange: [80, 200],
  cooldownRange: [400, 1200],
  trailFadeMs: 350,
};

// ── Polygon class ────────────────────────────────────────────────────────────

export class Polygon {
  protected _nodes: PolyNode[] = [];
  protected _edges: PolyEdge[] = [];
  /** adjacency[nodeIdx] → array of { neighborIdx, edgeIdx }. */
  protected _adjacency: Map<number, { neighbor: number; edge: number }[]> = new Map();

  protected _particles: Particle[] = [];
  protected _trails: EdgeTrail[] = [];

  protected _config: PolygonConfig;
  protected _lastTick = 0;
  protected _rng: () => number;

  constructor(
    nodes: PolyNode[],
    edges: PolyEdge[],
    config: Partial<PolygonConfig> = {},
  ) {
    this._config = { ...DEFAULT_POLYGON_CONFIG, ...config };
    this._rng = Math.random; // seeded RNG can be injected later
    this.setGraph(nodes, edges);
  }

  // ── Graph management ────────────────────────────────────────────────────

  /** Update config values (applied on next setGraph). */
  configure(overrides: Partial<PolygonConfig>): void {
    Object.assign(this._config, overrides);
  }

  /** Replace graph and respawn particles. */
  setGraph(nodes: PolyNode[], edges: PolyEdge[]): void {
    this._nodes = nodes;
    this._edges = edges;
    this._buildAdjacency();
    this._spawnParticles();
    this._trails = [];
  }

  /** Update node positions without respawning particles. */
  updateNodes(nodes: PolyNode[]): void {
    this._nodes = nodes;
    // adjacency stays the same (topology unchanged)
  }

  private _buildAdjacency(): void {
    this._adjacency.clear();
    for (let i = 0; i < this._nodes.length; i++) {
      this._adjacency.set(i, []);
    }
    for (let ei = 0; ei < this._edges.length; ei++) {
      const e = this._edges[ei];
      this._adjacency.get(e.a)!.push({ neighbor: e.b, edge: ei });
      this._adjacency.get(e.b)!.push({ neighbor: e.a, edge: ei });
    }
  }

  private _spawnParticles(): void {
    this._particles = [];
    if (this._nodes.length === 0) return;

    const count = Math.min(this._config.particleCount, this._nodes.length);
    const used = new Set<number>();

    for (let i = 0; i < count; i++) {
      let nodeIdx: number;
      let attempts = 0;
      do {
        nodeIdx = Math.floor(this._rng() * this._nodes.length);
        attempts++;
      } while (used.has(nodeIdx) && attempts < this._nodes.length * 2);
      used.add(nodeIdx);

      this._particles.push({
        homeNode: nodeIdx,
        state: 'resting',
        targetNode: -1,
        edgeIdx: -1,
        jumpStart: 0,
        jumpDur: 0,
        bouncing: false,
        cooldown: this._randRange(this._config.cooldownRange),
      });
    }
  }

  // ── Time evolution ──────────────────────────────────────────────────────

  tick(now: number): void {
    if (this._lastTick === 0) { this._lastTick = now; return; }
    const dt = now - this._lastTick;
    this._lastTick = now;

    // Advance particles
    for (const p of this._particles) {
      if (p.state === 'jumping') {
        this._tickJumping(p, now, dt);
      } else {
        this._tickResting(p, now, dt);
      }
    }

    // Fade completed trails
    this._fadeTrails(now);
  }

  private _tickResting(p: Particle, now: number, dt: number): void {
    p.cooldown -= dt;
    if (p.cooldown > 0) return;

    // Probabilistic jump attempt
    if (this._rng() > this._config.jumpProbability) return;

    const neighbors = this._adjacency.get(p.homeNode);
    if (!neighbors || neighbors.length === 0) return;

    // Pick a random neighbor
    const pick = neighbors[Math.floor(this._rng() * neighbors.length)];
    const targetOccupied = this._particles.some(
      q => q !== p && q.state === 'resting' && q.homeNode === pick.neighbor,
    );

    p.state = 'jumping';
    p.targetNode = pick.neighbor;
    p.edgeIdx = pick.edge;
    p.jumpStart = now;
    p.jumpDur = this._randRange(this._config.jumpDurationRange);
    p.bouncing = targetOccupied;

    // Create the edge trail
    const e = this._edges[pick.edge];
    const dir: 1 | -1 = e.a === p.homeNode ? 1 : -1;
    this._trails.push({
      edgeIdx: pick.edge,
      direction: dir,
      head: 0,
      tail: 0,
      alpha: 1,
      birth: now,
    });
  }

  private _tickJumping(p: Particle, now: number, _dt: number): void {
    const elapsed = now - p.jumpStart;
    const totalDur = p.bouncing ? p.jumpDur * 2 : p.jumpDur;

    if (elapsed >= totalDur) {
      // Jump complete
      if (!p.bouncing) {
        p.homeNode = p.targetNode;
      }
      // else: bounce back to origin (homeNode stays)
      p.state = 'resting';
      p.cooldown = this._randRange(this._config.cooldownRange);
      return;
    }

    // Update trail head/tail position
    const trail = this._trails.find(
      tr => tr.edgeIdx === p.edgeIdx && tr.birth === p.jumpStart,
    );
    if (trail) {
      const t = elapsed / p.jumpDur; // 0→1 for first leg
      if (p.bouncing && elapsed > p.jumpDur) {
        // Return leg
        const retT = (elapsed - p.jumpDur) / p.jumpDur;
        trail.head = 1 - retT;
        trail.tail = 1;
      } else {
        trail.head = Math.min(1, t);
        trail.tail = Math.max(0, t - 0.6); // trail body length
      }
    }
  }

  private _fadeTrails(now: number): void {
    const fadeMs = this._config.trailFadeMs;
    for (let i = this._trails.length - 1; i >= 0; i--) {
      const tr = this._trails[i];
      const age = now - tr.birth;
      // Trail fades after the jump is over
      const jumpDone = !this._particles.some(
        p => p.state === 'jumping' && p.edgeIdx === tr.edgeIdx && p.jumpStart === tr.birth,
      );
      if (jumpDone) {
        const fadeStart = age; // rough — starts fading once jump completes
        tr.alpha = Math.max(0, 1 - fadeStart / fadeMs);
        if (tr.alpha <= 0) {
          this._trails.splice(i, 1);
        }
      }
    }
  }

  // ── Snapshot ────────────────────────────────────────────────────────────

  snapshot(): PolygonSnapshot {
    const occupied = new Set<number>();
    const particles: ParticleView[] = [];

    for (const p of this._particles) {
      if (p.state === 'resting') {
        occupied.add(p.homeNode);
        const n = this._nodes[p.homeNode];
        particles.push({ x: n.x, y: n.y, homeNode: p.homeNode, resting: true });
      } else {
        // Interpolate position along edge
        occupied.add(p.homeNode); // node is still "logically" owned
        const e = this._edges[p.edgeIdx];
        const from = this._nodes[e.a === p.homeNode ? e.a : e.b];
        const to = this._nodes[e.a === p.homeNode ? e.b : e.a];
        const elapsed = (this._lastTick - p.jumpStart);
        let t = Math.min(1, elapsed / p.jumpDur);
        if (p.bouncing && elapsed > p.jumpDur) {
          t = 1 - Math.min(1, (elapsed - p.jumpDur) / p.jumpDur);
        }
        particles.push({
          x: from.x + (to.x - from.x) * t,
          y: from.y + (to.y - from.y) * t,
          homeNode: p.homeNode,
          resting: false,
        });
      }
    }

    return {
      nodes: this._nodes,
      edges: this._edges,
      occupied,
      particles,
      trails: [...this._trails],
    };
  }

  // ── Utilities ───────────────────────────────────────────────────────────

  protected _randRange([min, max]: [number, number]): number {
    return min + this._rng() * (max - min);
  }
}
